import React from "react";
import { Button } from "../../atoms/Button/index.js";
import { Skeleton } from "../../atoms/Skeleton/index.js";
import { EYEBROW } from "../../lib/typography.js";
import { cn } from "../../lib/utils.js";
import { EmptyState } from "../EmptyState/index.js";
import type { PeopleFieldResolvedLabels } from "./people-field-labels.js";
import type { PeopleFieldStatus as Status } from "./people-field-model.js";

/* The workspace search's own states, below the listbox (a listbox may own
   only options, and Retry is a button). Invariant 8: loading is a named
   status region with the shape of what is coming, a failure is EmptyState's
   error row with a Retry and never an Alert, and an empty search is not
   allowed to stand in for a failed one. */

const SKELETON_ROWS = [
  { name: "w-2/5", detail: "w-3/5" },
  { name: "w-1/3", detail: "w-1/2" },
];

export function PeopleFieldStatus({
  status,
  title,
  query,
  hasRoster,
  labels,
  onRetry,
  onRetryKeyDown,
}: {
  readonly status: Status;
  /** The workspace section's title, drawn over the skeleton rows. */
  readonly title: string;
  readonly query: string;
  /** People with access still show when the search fails. */
  readonly hasRoster: boolean;
  readonly labels: PeopleFieldResolvedLabels;
  readonly onRetry: () => void;
  readonly onRetryKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}): React.ReactElement | null {
  if (status === "loading") {
    return (
      <div role="status" aria-label={labels.searching}>
        <div
          aria-hidden="true"
          className={cn(
            "flex items-center justify-between px-2 pb-1 pt-2",
            EYEBROW,
          )}
        >
          <span>{title}</span>
          <span>{labels.searchingCount}</span>
        </div>
        {SKELETON_ROWS.map((row) => (
          <div
            key={row.name}
            aria-hidden="true"
            className="flex min-h-11 items-center gap-2.5 px-2 py-1.5"
          >
            <Skeleton shape="circle" className="w-6 shrink-0" />
            <span className="flex flex-1 flex-col gap-1.5">
              <Skeleton className={cn("h-2.5", row.name)} />
              <Skeleton className={cn("h-2.5", row.detail)} />
            </span>
          </div>
        ))}
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="px-2 py-2">
        <EmptyState
          variant="error"
          size="sm"
          role="alert"
          title={
            hasRoster
              ? `${labels.searchFailed} ${labels.searchFailedRoster}`
              : labels.searchFailed
          }
          action={
            <Button
              variant="ghost"
              size="xs"
              onClick={onRetry}
              onKeyDown={onRetryKeyDown}
            >
              {labels.retry}
            </Button>
          }
        />
      </div>
    );
  }
  if (status === "empty") {
    return (
      <div className="px-2 py-3">
        <EmptyState
          variant="no-results"
          size="sm"
          role="status"
          title={labels.noResults(query)}
          description={labels.noResultsHint}
        />
      </div>
    );
  }
  return null;
}
